class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();

    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;

      case 'ArrowDown':
        this.switchOption('down');
        break;

      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const element = this.querySelector('[data-predictive-search-search-for-text]');
    const currentText = element?.innerText;

    if (!currentText || !previousTerm) return;

    const matches = currentText.match(new RegExp(previousTerm, 'g'));

    if (matches?.length > 1) return;

    element.innerText = currentText.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const elements = Array.from(
      this.querySelectorAll('li[role="option"], button.predictive-search__item, a.predictive-search__item[role="option"]')
    ).filter((element) => element.offsetParent !== null);

    if (!elements.length) return;
    if (moveUp && !selectedElement) return;

    let index = selectedElement ? elements.indexOf(selectedElement) : -1;

    if (moveUp) {
      index = index <= 0 ? elements.length - 1 : index - 1;
    } else {
      index = index >= elements.length - 1 ? 0 : index + 1;
    }

    selectedElement?.setAttribute('aria-selected', 'false');

    const activeElement = elements[index];

    activeElement.setAttribute('aria-selected', 'true');

    this.input.setAttribute(
      'aria-activedescendant',
      activeElement.id || ''
    );
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, a[aria-selected="true"], button[aria-selected="true"]'
    );

    selectedOption?.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);

      const deferred = this.dispatchSearchUpdateEvent(searchTerm);

      deferred?.resolve({
        totalCount: this.getTotalResultCount()
      });

      return;
    }

    const deferred = this.dispatchSearchUpdateEvent(searchTerm);

    const isArabic =
      document.documentElement.lang?.toLowerCase().startsWith('ar') ||
      window.location.pathname.startsWith('/ar/');

    if (isArabic) {
      this.getArabicSearchResults(searchTerm, queryKey, deferred);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
      {
        signal: this.abortController.signal
      }
    )
      .then((response) => {
        if (!response.ok) throw new Error(response.status);

        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');

        const section = doc.querySelector(
          '#shopify-section-predictive-search'
        );

        if (!section) {
          throw new Error('Predictive search section not found');
        }

        const markup = section.innerHTML;

        this.cacheResults(queryKey, markup);
        this.renderSearchResults(markup);

        deferred?.resolve({
          totalCount: this.getTotalResultCount()
        });
      })
      .catch((error) => {
        if (error.name === 'AbortError' || error?.code === 20) {
          deferred?.reject(error);
          return;
        }

        console.error('Predictive search error:', error);

        deferred?.reject(error);
        this.close();
      });
  }

  getArabicSearchResults(searchTerm, queryKey, deferred) {
    const root = (routes.root_url || '/ar/').replace(/\/$/, '');

    const url =
      `${root}/search?q=${encodeURIComponent(searchTerm)}` +
      `&type=product,page,article` +
      `&options[prefix]=last`;

    fetch(url, {
      signal: this.abortController.signal
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);

        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');

        const resultsRoot =
          doc.querySelector('.template-search__results') ||
          doc.querySelector('main') ||
          doc.body;

        const products = this.extractArabicProducts(resultsRoot);
        const content = this.extractArabicContent(resultsRoot);

        const pages = content.pages;
        const articles = content.articles;

        const suggestions = this.buildArabicSuggestions(
          searchTerm,
          pages,
          articles,
          products
        );

        const total =
          products.length +
          pages.length +
          articles.length;

        const markup = total
          ? this.buildArabicMarkup(
              searchTerm,
              root,
              products,
              pages,
              articles,
              suggestions,
              total
            )
          : this.buildArabicNoResults(searchTerm, root);

        this.cacheResults(queryKey, markup);
        this.renderSearchResults(markup);

        deferred?.resolve({
          totalCount: total
        });
      })
      .catch((error) => {
        if (error.name === 'AbortError' || error?.code === 20) {
          deferred?.reject(error);
          return;
        }

        console.error('Arabic search fallback error:', error);

        deferred?.reject(error);
        this.close();
      });
  }

  extractArabicProducts(root) {
    const products = [];
    const seen = new Set();

    const cards = root.querySelectorAll(
      '.card-wrapper, .product-card-wrapper'
    );

    for (const card of cards) {
      const link = card.querySelector('a[href*="/products/"]');

      if (!link) continue;

      const href = link.getAttribute('href');

      if (!href || seen.has(href)) continue;

      const title =
        card.querySelector('.card__heading, h3, h2') ||
        link;

      const image = card.querySelector('img');
      const price = card.querySelector('.price');

      products.push({
        href,
        title: title.textContent.replace(/\s+/g, ' ').trim(),
        image:
          image?.getAttribute('src') ||
          image?.dataset?.src ||
          '',
        price: price?.innerHTML || ''
      });

      seen.add(href);

      if (products.length >= 6) break;
    }

    return products;
  }

  extractArabicContent(root) {
    const pages = [];
    const articles = [];
    const seen = new Set();

    const links = root.querySelectorAll(
      'a[href*="/pages/"], a[href*="/blogs/"]'
    );

    for (const link of links) {
      const href = link.getAttribute('href');

      if (!href || seen.has(href)) continue;

      const container =
        link.closest('.card-wrapper, .article-card-wrapper, .card, li') ||
        link.parentElement;

      const titleElement =
        container?.querySelector('.card__heading, h3, h2') ||
        link;

      const title =
        titleElement.textContent.replace(/\s+/g, ' ').trim();

      if (!title) continue;

      if (href.includes('/pages/') && pages.length < 4) {
        pages.push({ href, title });
        seen.add(href);
      }

      if (href.includes('/blogs/') && articles.length < 4) {
        articles.push({ href, title });
        seen.add(href);
      }

      if (pages.length >= 4 && articles.length >= 4) break;
    }

    return {
      pages,
      articles
    };
  }

  buildArabicSuggestions(searchTerm, pages, articles, products) {
    const query = searchTerm.trim().toLowerCase();
    const suggestions = [];
    const seen = new Set();

    const candidates = [
      ...pages,
      ...articles,
      ...products
    ];

    for (const item of candidates) {
      const title = item.title.trim();
      const normalized = title.toLowerCase();

      if (
        !title ||
        normalized === query ||
        seen.has(normalized)
      ) {
        continue;
      }

      suggestions.push(title);
      seen.add(normalized);

      if (suggestions.length >= 3) break;
    }

    return suggestions;
  }

  buildArabicMarkup(
    searchTerm,
    root,
    products,
    pages,
    articles,
    suggestions,
    total
  ) {
    const suggestionsHtml = suggestions.length
      ? this.buildTextGroup(
          'اقتراحات',
          suggestions.map((title) => ({
            title,
            href:
              `${root}/search?q=${encodeURIComponent(title)}`
          })),
          'suggestion'
        )
      : '';

    const pagesHtml = pages.length
      ? this.buildTextGroup(
          'الصفحات',
          pages,
          'page'
        )
      : '';

    const articlesHtml = articles.length
      ? this.buildTextGroup(
          'المقالات',
          articles,
          'article'
        )
      : '';

    const productsHtml = products.length
      ? `
        <div class="predictive-search__result-group predictive-search__arabic-products">

          <h2 class="predictive-search__heading caption-large text-body">
            المنتجات
          </h2>

          <ul class="predictive-search__results-list list-unstyled" role="group">

            ${products
              .map(
                (product, index) => `
                  <li
                    id="predictive-search-product-${index}"
                    role="option"
                    aria-selected="false"
                  >

                    <a
                      href="${this.escapeHtml(product.href)}"
                      class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text"
                      tabindex="-1"
                    >

                      ${
                        product.image
                          ? `
                            <img
                              class="predictive-search__image"
                              src="${this.escapeHtml(product.image)}"
                              alt=""
                              width="50"
                              height="50"
                              loading="lazy"
                            >
                          `
                          : ''
                      }

                      <div class="predictive-search__item-content">

                        <p class="predictive-search__item-heading h5">
                          ${this.escapeHtml(product.title)}
                        </p>

                        ${
                          product.price
                            ? `
                              <div class="predictive-search__price">
                                ${product.price}
                              </div>
                            `
                            : ''
                        }

                      </div>

                    </a>

                  </li>
                `
              )
              .join('')}

          </ul>

        </div>
      `
      : '';

    return `
      <div
        id="predictive-search-results"
        data-total-results="${total}"
      >

        <div
          id="predictive-search-results-groups-wrapper"
          class="predictive-search__results-groups-wrapper"
        >

          ${
            suggestionsHtml || pagesHtml || articlesHtml
              ? `
                <div class="predictive-search__result-group">
                  ${suggestionsHtml}
                  ${pagesHtml}
                  ${articlesHtml}
                </div>
              `
              : ''
          }

          ${productsHtml}

        </div>

        <a
          href="${root}/search?q=${encodeURIComponent(searchTerm)}"
          class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow"
          tabindex="-1"
          role="option"
          aria-selected="false"
        >
          البحث عن &ldquo;${this.escapeHtml(searchTerm)}&rdquo;
        </a>

        <span
          class="visually-hidden"
          data-predictive-search-live-region-count-value
        >
          ${total} نتائج
        </span>

      </div>
    `;
  }

  buildTextGroup(title, items, type) {
    return `
      <div class="predictive-search__arabic-section">

        <h2 class="predictive-search__heading caption-large text-body">
          ${title}
        </h2>

        <ul
          class="predictive-search__results-list list-unstyled"
          role="group"
        >

          ${items
            .map(
              (item, index) => `
                <li
                  id="predictive-search-${type}-${index}"
                  role="option"
                  aria-selected="false"
                >

                  <a
                    href="${this.escapeHtml(item.href)}"
                    class="predictive-search__item link link--text"
                    tabindex="-1"
                  >

                    <div class="predictive-search__item-content">

                      <p class="predictive-search__item-heading h5">
                        ${this.escapeHtml(item.title)}
                      </p>

                    </div>

                  </a>

                </li>
              `
            )
            .join('')}

        </ul>

      </div>
    `;
  }

  buildArabicNoResults(searchTerm, root) {
    return `
      <div
        id="predictive-search-results"
        data-total-results="0"
      >

        <p class="predictive-search__heading">
          لا توجد نتائج
        </p>

        <a
          href="${root}/search?q=${encodeURIComponent(searchTerm)}"
          class="predictive-search__item predictive-search__item--term link link--text h5"
        >
          البحث عن &ldquo;${this.escapeHtml(searchTerm)}&rdquo;
        </a>

        <span
          class="visually-hidden"
          data-predictive-search-live-region-count-value
        >
          لا توجد نتائج
        </span>

      </div>
    `;
  }

  cacheResults(queryKey, markup) {
    this.allPredictiveSearchInstances.forEach((instance) => {
      instance.cachedResults[queryKey] = markup;
    });
  }

  escapeHtml(value = '') {
    const div = document.createElement('div');

    div.textContent = value;

    return div.innerHTML;
  }

  getTotalResultCount() {
    return (
      parseInt(
        this.predictiveSearchResults.querySelector(
          '[data-total-results]'
        )?.dataset.totalResults
      ) || 0
    );
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};

    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise
      })
    );

    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector('.predictive-search-status');

    this.loadingText =
      this.loadingText ||
      this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);

    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute(
      'aria-hidden',
      'false'
    );

    this.statusElement.textContent =
      statusText || '';

    setTimeout(() => {
      this.statusElement?.setAttribute(
        'aria-hidden',
        'true'
      );
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML =
      resultsMarkup;

    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const liveRegion =
      this.querySelector(
        '[data-predictive-search-live-region-count-value]'
      );

    this.setLiveRegionText(
      liveRegion?.textContent || ''
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      (
        document
          .querySelector('.section-header')
          ?.getBoundingClientRect()
          .bottom || 0
      );

    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight ||
      `${this.getResultsMaxHeight()}px`;

    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);

    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected =
      this.querySelector(
        '[aria-selected="true"]'
      );

    selected?.setAttribute(
      'aria-selected',
      'false'
    );

    this.input.setAttribute(
      'aria-activedescendant',
      ''
    );

    this.removeAttribute('loading');
    this.removeAttribute('open');

    this.input.setAttribute(
      'aria-expanded',
      'false'
    );

    this.resultsMaxHeight = false;

    this.predictiveSearchResults.removeAttribute(
      'style'
    );
  }
}

customElements.define(
  'predictive-search',
  PredictiveSearch
);